import { CommonModule } from '@angular/common';
import { Component, computed, effect, EventEmitter, forwardRef, Input, Output, signal, Signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatInputModule } from '@angular/material/input';
import { TranslateModule } from '@ngx-translate/core';
import { GalaxyAbstractFilterInput, GalaxyFilterValueInterface } from '@vendasta/galaxy/filter/chips';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import {
  PartnerCRMSalespersonService,
  salespersonFilterFn,
  SalespersonWithDisplayName,
} from '../../salesperson.service';

@Component({
  template: `
    <glxy-form-field bottomSpacing="'none'">
      <input
        [placeholder]="'TABLE_FILTERS.SELECT_EXPRESSION_PLACEHOLDER' | translate"
        type="text"
        matInput
        [formControl]="formControl"
        [matAutocomplete]="auto"
        data-testid="filter-salesperson-input"
      />
      <mat-autocomplete #auto="matAutocomplete" [displayWith]="displayFn(availableOptions())">
        <mat-option *ngFor="let option of filteredOptions()" [value]="option">
          {{ option?.displayName }}
        </mat-option>
      </mat-autocomplete>
    </glxy-form-field>
  `,
  providers: [
    {
      provide: GalaxyAbstractFilterInput,
      useExisting: forwardRef(() => SalespersonFilterInputComponent),
    },
  ],
  imports: [
    CommonModule,
    TranslateModule,
    ReactiveFormsModule,
    MatInputModule,
    GalaxyFormFieldModule,
    MatAutocompleteModule,
  ],
  standalone: true,
})
export class SalespersonFilterInputComponent implements GalaxyAbstractFilterInput {
  @Input() set fieldId(value: string) {
    this._fieldId.set(value);
  }

  @Output() valueChanges = new EventEmitter<GalaxyFilterValueInterface[]>();
  formControl: FormControl<SalespersonWithDisplayName | string> = new FormControl<SalespersonWithDisplayName | string>(
    '',
    {
      nonNullable: true,
    },
  );

  searchTerm = toSignal(this.formControl.valueChanges, { initialValue: '' });
  _fieldId = signal('');
  availableOptions = signal<SalespersonWithDisplayName[]>([]);

  @Input() set initialValue(value: GalaxyFilterValueInterface[]) {
    if (value?.length > 0 && value[0].string) {
      this.formControl.setValue(value[0].string);
    }
  }

  filteredOptions: Signal<SalespersonWithDisplayName[]> = computed(() => {
    const searchTerm = this.searchTerm();
    const availableOptions = this.availableOptions();
    const filterTerm = typeof searchTerm === 'string' ? searchTerm : searchTerm?.salespersonId;
    return availableOptions.filter((salesperson) => salespersonFilterFn(salesperson, filterTerm));
  });

  constructor(private readonly salespersonService: PartnerCRMSalespersonService) {
    effect(
      () => {
        const value = this.searchTerm();
        if (value === '') {
          this.valueChanges.emit([]);
          return;
        }
        if (typeof value === 'string') {
          this.valueChanges.emit([{ string: value }]);
          return;
        }
        this.valueChanges.emit([{ string: value?.salespersonId }]);
      },
      { allowSignalWrites: true },
    );

    this.salespersonService.allSalespeople$$
      .pipe(takeUntilDestroyed())
      .subscribe((allSalespeople) => this.availableOptions.set(allSalespeople));
  }

  clearValue() {
    this.formControl.setValue('');
  }

  displayFn(
    availableOptions: SalespersonWithDisplayName[],
  ): (salesperson: SalespersonWithDisplayName | string) => string {
    return (salesperson: SalespersonWithDisplayName | string): string => {
      if (!salesperson) {
        return '';
      }
      if (typeof salesperson === 'string') {
        const foundSalesperson = availableOptions.find((sp) => sp.salespersonId === salesperson);
        return foundSalesperson?.displayName || salesperson;
      }
      return salesperson?.displayName;
    };
  }
}
